import { Banner, Button, Field, Input } from '@cloudflare/kumo';
import { FormEvent, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { DangerButton } from '../components/ui/DangerButton';
import { ICONS } from '../components/ui/icons';

interface Subject {
  phone: string; generatedAt: string;
  contacts: Array<{ id: string; nome: string; telefone: string; email: string | null; categoria: string | null; consentimento: { em: string; origem: string; texto: string } | null }>;
  conversations: Array<{ id: string; channel: string; status: string; messages: unknown[] }>;
  optOuts: Array<{ id: string; reason: string | null; createdAt: string }>;
  interactiveSessions: unknown[]; autoReplyConversations: unknown[]; birthdayMessages: unknown[];
  payments: unknown[];
}

const ORIGEM: Record<string, string> = { KEYWORD: 'palavra-chave', FORM: 'formulário', API: 'API', IMPORT: 'importação', MANUAL: 'manual' };

/** Contatos › LGPD: acesso, portabilidade e eliminação dos dados de um titular */
export function LgpdPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [phone, setPhone] = useState('');
  const [data, setData] = useState<Subject | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const allowed = can('contacts:write');

  const search = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await fetchApi(`/api/lgpd/titular?phone=${encodeURIComponent(phone)}`);
      const out = await r.json();
      if (!r.ok) throw new Error(out.message || 'Não foi possível buscar');
      setData(out);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível buscar');
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dados-titular-${data.phone}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const forget = async () => {
    if (!data) return;
    setConfirm(false);
    const r = await fetchApi('/api/lgpd/titular/esquecer', { method: 'POST', body: JSON.stringify({ phone: data.phone, confirm: 'ESQUECER' }) });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(out.message || 'Não foi possível eliminar'); return; }
    toast.success(`Dados eliminados: ${out.removed.contacts} contato(s) e ${out.removed.conversations} conversa(s) com ${out.removed.conversationMessages} mensagem(ns)`);
    setData(null);
    setPhone('');
  };

  const total = data ? data.contacts.length + data.conversations.length + data.interactiveSessions.length + data.autoReplyConversations.length + data.birthdayMessages.length : 0;

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>LGPD · direitos do titular</h2>
        <div style={{ color: 'var(--text-muted)', maxWidth: 780 }}>
          Quando alguém pedir, encontre tudo o que a organização guarda sobre o número dele, entregue uma cópia (acesso e portabilidade) ou elimine os dados. Na eliminação, o número continua descadastrado para não receber mais mensagens, e as cobranças Pix ficam guardadas por obrigação legal.
        </div>
      </div>

      <div className="page-content space-y-6" style={{ maxWidth: 900 }}>
        {!allowed && <Banner variant="alert" description={<>Você precisa de permissão para editar contatos.</>} />}
        <form onSubmit={search} className="flex gap-2" style={{ alignItems: 'end' }}>
          <div style={{ flex: 1, maxWidth: 320 }}>
            <Field label="Telefone do titular">
              <Input onChange={(e) => setPhone(e.target.value)} placeholder="(63) 99999-0000" value={phone} disabled={!allowed} />
            </Field>
          </div>
          <Button type="submit" variant="primary" icon={ICONS.MagnifyingGlass} loading={busy} disabled={!allowed || phone.replace(/\D/g, '').length < 10}>Buscar</Button>
        </form>

        {data && (
          <section className="brand-card" style={{ padding: 20 }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h4 className="text-base font-semibold">+{data.phone}</h4>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{total === 0 ? 'Nenhum dado encontrado para este número.' : `${total} registro(s) encontrado(s).`}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" icon={ICONS.Download} onClick={download}>Baixar dados (JSON)</Button>
                <DangerButton disabled={total === 0} onClick={() => setConfirm(true)}>Eliminar dados</DangerButton>
              </div>
            </div>
            <ul className="space-y-2" style={{ marginTop: 16, fontSize: 14 }}>
              {data.contacts.map((c) => (
                <li key={c.id}>
                  <strong>{c.nome}</strong> · {c.telefone}{c.email ? ` · ${c.email}` : ''}{c.categoria ? ` · categoria ${c.categoria}` : ''}
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {c.consentimento
                      ? `Consentimento em ${new Date(c.consentimento.em).toLocaleString('pt-BR')} por ${ORIGEM[c.consentimento.origem] || c.consentimento.origem}: “${c.consentimento.texto}”`
                      : 'Sem consentimento registrado.'}
                  </div>
                </li>
              ))}
              <li>{data.conversations.length} conversa(s) na caixa de entrada, com {data.conversations.reduce((n, c) => n + c.messages.length, 0)} mensagem(ns)</li>
              <li>{data.interactiveSessions.length} conversa(s) em fluxo interativo · {data.autoReplyConversations.length} conversa(s) com respostas automáticas · {data.birthdayMessages.length} mensagem(ns) de aniversário</li>
              <li>{data.optOuts.length ? 'Descadastrado (não recebe mensagens).' : 'Não está descadastrado.'} {data.payments.length ? `${data.payments.length} cobrança(s) Pix (ficam guardadas).` : ''}</li>
            </ul>
          </section>
        )}
      </div>

      <DangerConfirmModal open={confirm} title="Eliminar os dados deste titular" confirmText="Eliminar" requiredText="ESQUECER" onCancel={() => setConfirm(false)} onConfirm={forget}>
        {data ? `Apaga os contatos e as conversas de +${data.phone}. Não dá para desfazer. O número fica descadastrado.` : ''}
      </DangerConfirmModal>
    </>
  );
}
